import type { CSSProperties } from "react";
import {
  AnthropicLogo,
  OpenAILogo,
  GeminiLogo,
  MistralLogo,
  DeepSeekLogo,
  PerplexityLogo,
} from "@/components/brand/llm-logos";
import { CohereLogo } from "@/components/brand/llm-logos/Cohere";
import { KimiLogo } from "@/components/brand/llm-logos/Kimi";

/**
 * Sprint UX 2026-06-07 — affiche la "maison IA" (logo + nom) sous chaque
 * persona du Conseil. Hōjin n'est pas un débat de LLM : c'est un débat
 * entre les grandes IA mondiales. Cette signature doit être visible
 * partout où un persona est nommé (Foyer §III, bandeau Conseil, bulles
 * de délibération, landing).
 */

export type PersonaId =
  | "delphine"
  | "henri"
  | "akilah"
  | "eva"
  | "ren"
  | "julien"
  | "imani"
  | "emile";

type Entry = {
  provider: string; // libellé "maison"
  Logo: typeof AnthropicLogo;
};

const REGISTRY: Record<PersonaId, Entry> = {
  delphine: { provider: "Anthropic", Logo: AnthropicLogo },
  henri:    { provider: "OpenAI",    Logo: OpenAILogo },
  akilah:   { provider: "Google",    Logo: GeminiLogo },
  eva:      { provider: "Mistral AI", Logo: MistralLogo },
  ren:      { provider: "DeepSeek",  Logo: DeepSeekLogo },
  julien:   { provider: "Perplexity", Logo: PerplexityLogo },
  imani:    { provider: "Cohere",    Logo: CohereLogo },
  emile:    { provider: "Moonshot",  Logo: KimiLogo },
};

type Props = {
  personaId: string;
  logoSize?: number;
  fontSize?: number;
  color?: string;
  align?: "start" | "center" | "end";
  style?: CSSProperties;
  /** Inverser : logo à droite. */
  reverse?: boolean;
};

export function PersonaProvider({
  personaId,
  logoSize = 14,
  fontSize = 11,
  color = "var(--smoke)",
  align = "center",
  style,
  reverse = false,
}: Props) {
  const entry = REGISTRY[personaId as PersonaId];
  if (!entry) return null;
  const { Logo, provider } = entry;
  const justify = align === "start" ? "flex-start" : align === "end" ? "flex-end" : "center";
  return (
    <span
      aria-label={`Maison IA : ${provider}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        justifyContent: justify,
        flexDirection: reverse ? "row-reverse" : "row",
        color,
        fontFamily: '"DM Mono", monospace',
        fontSize,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        ...style,
      }}
    >
      <Logo width={logoSize} height={logoSize} aria-hidden />
      <span>{provider}</span>
    </span>
  );
}

export function getPersonaProvider(personaId: string): Entry | null {
  return REGISTRY[personaId as PersonaId] ?? null;
}
